import React, { useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import type { MappedLeadData } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useLeadDetail } from '../../hooks/useLeadDetail';

/** Replacement provenance, using the same cached detail query as the full lead view. */
export const ReplacementLeadBadge: React.FC<{ lead: MappedLeadData; campaignId?: string }> = ({ lead, campaignId }) => {
  const { currentUser, targetEmail } = useAuth();
  const tooltipId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const showTooltip = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setPosition({ left: Math.max(16, Math.min(rect.left, window.innerWidth - 296)), top: rect.bottom + 8 });
  };
  const replacedId = lead.replaces ? String(lead.replaces) : '';
  const reason = lead.replaces_reason?.trim();
  const { data: replacedLead, isLoading } = useLeadDetail(
    campaignId || lead.campaignId || '',
    replacedId,
    targetEmail || currentUser?.email || '',
  );

  if (!replacedId && !reason) return null;

  return (
    <div className="group/replacement-badge absolute -right-2 top-0 z-30 text-left">
      <button
        ref={triggerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={() => setPosition(null)}
        onFocus={showTooltip}
        onBlur={() => setPosition(null)}
        onKeyDown={(event) => { if (event.key === 'Escape') setPosition(null); }}
        type="button"
        aria-label={replacedId ? `Replaces Lead #${replacedId}` : 'Replacement lead'}
        aria-describedby={tooltipId}
        className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-emerald-400/30 bg-black/75 text-emerald-300 shadow-[0_0_20px_rgba(16,185,129,0.16)] backdrop-blur-md transition-colors hover:bg-emerald-500/12 hover:text-emerald-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/50"
      >
        <Icon icon="bx:transfer-alt" className="h-4 w-4" aria-hidden="true" />
      </button>
      <>
      {position && createPortal(<div id={tooltipId} role="tooltip" style={position} className="pointer-events-none fixed z-[10001] text-left w-[280px] max-w-[calc(100vw-2rem)] rounded-2xl border border-emerald-500/20 bg-[#0B1110]/96 p-4 shadow-[0_20px_60px_rgba(0,0,0,0.45)] backdrop-blur-xl">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-300">Replaced Lead</div>
        <div className="mt-1 break-words text-sm font-semibold text-white">{replacedLead?.leadName?.trim() || (replacedId ? `Lead #${replacedId}` : 'Lead marked as replacement during enrichment')}</div>
        {isLoading ? <div className="mt-0.5 text-xs text-gray-400">Loading replaced lead details...</div> : <>
          {replacedLead?.leadRole && <div className="mt-0.5 text-xs text-gray-300">{replacedLead.leadRole}</div>}
          {replacedLead?.companyName && <div className="mt-0.5 text-[11px] text-gray-400">{replacedLead.companyName}</div>}
        </>}
        <div className="mt-4 rounded-xl border border-white/8 bg-white/[0.03] px-3 py-2.5">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-300">Why This Lead Replaced {replacedId ? `#${replacedId}` : 'Another Lead'}</div>
          <p className="mt-2 whitespace-pre-wrap break-words text-xs leading-5 text-gray-200">{reason || 'This lead was identified during enrichment as the stronger contact for this campaign.'}</p>
        </div>
      </div>, document.body)}
      </>
    </div>
  );
};
